// ScreenshotUpload.js
// Upload component for trade screenshots with tier-based access control.
// Handles drag-and-drop, file validation, upload progress, and deletion.
//
// Tier limits (enforced server-side; mirrored here for UX):
//   free: 1 per trade, no AI analysis
//   basic: 1 per trade + AI Vision analysis
//   pro: 3 per trade + AI Vision analysis
//   elite: 5 per trade + AI Vision analysis

import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader2, Image, Sparkles, AlertCircle } from 'lucide-react';
import { uploadTradeScreenshots, deleteTradeScreenshot } from '../../services/screenshotService';
import ScreenshotThumbnail from './ScreenshotThumbnail';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const SCREENSHOTS_PER_TRADE = { free: 1, basic: 1, pro: 3, elite: 5 };

/**
 * @param {Object}   props
 * @param {number|string} props.tradeId
 * @param {Array}    props.screenshots       - Existing screenshots from DB
 * @param {string}   props.planId            - 'free' | 'basic' | 'pro' | 'elite'
 * @param {function} props.onScreenshotsChange - Callback(newScreenshots: Array)
 * @param {string}   [props.className]
 */
const ScreenshotUpload = ({
  tradeId,
  screenshots = [],
  planId = 'free',
  onScreenshotsChange,
  className = '',
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const limit = SCREENSHOTS_PER_TRADE[planId] ?? 0;
  const canAnalyze = planId === 'basic' || planId === 'pro' || planId === 'elite';
  const canUpload = limit > 0;
  const remaining = Math.max(0, limit - screenshots.length);
  const atLimit = screenshots.length >= limit;

  // ─── Validation ────────────────────────────────────────────────────────────

  const validateFiles = (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return { valid: [], error: null };

    if (fileArray.length > remaining) {
      return {
        valid: [],
        error: `You can only add ${remaining} more screenshot${remaining === 1 ? '' : 's'} (plan limit: ${limit}/trade).`,
      };
    }

    const invalid = fileArray.filter(
      (f) => !ALLOWED_TYPES.includes(f.type) || f.size > MAX_FILE_SIZE
    );
    if (invalid.length > 0) {
      const reasons = invalid.map((f) => {
        if (!ALLOWED_TYPES.includes(f.type)) return `${f.name}: not an image`;
        return `${f.name}: exceeds 5MB limit`;
      });
      return { valid: [], error: reasons.join(', ') };
    }

    return { valid: fileArray, error: null };
  };

  // ─── Upload ────────────────────────────────────────────────────────────────

  const handleUpload = useCallback(
    async (files) => {
      const { valid, error: validationError } = validateFiles(files);
      if (validationError) {
        setError(validationError);
        return;
      }
      if (valid.length === 0) return;

      setError(null);
      setUploading(true);
      setUploadProgress(0);

      try {
        const newScreenshots = await uploadTradeScreenshots(tradeId, valid, setUploadProgress);
        onScreenshotsChange?.([...screenshots, ...newScreenshots]);
      } catch (err) {
        const msg = err?.response?.data?.error || err.message || 'Upload failed';
        setError(msg);
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [tradeId, screenshots, remaining]
  );

  // ─── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (screenshotId) => {
    try {
      await deleteTradeScreenshot(screenshotId);
      onScreenshotsChange?.(screenshots.filter((s) => s.id !== screenshotId));
    } catch (err) {
      setError(err?.response?.data?.error || 'Delete failed');
    }
  };

  // ─── Drag & Drop ───────────────────────────────────────────────────────────

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      if (!canUpload || atLimit) return;
      handleUpload(e.dataTransfer.files);
    },
    [canUpload, atLimit, handleUpload]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    if (canUpload && !atLimit) setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            Screenshots
          </span>
          <span className="text-xs text-muted-foreground">
            ({screenshots.length}/{limit})
          </span>
          {canAnalyze && (
            <span className="flex items-center gap-1 text-xs text-blue-400 bg-blue-500/10 rounded px-1.5 py-0.5">
              <Sparkles className="w-3 h-3" />
              AI Analysis
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">Max 5MB · JPEG, PNG, WebP, GIF</span>
      </div>

      {/* Existing thumbnails with delete buttons */}
      {screenshots.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {screenshots.map((shot) => (
            <div key={shot.id} className="relative group">
              <ScreenshotThumbnail screenshots={[shot]} maxVisible={1} />
              <button
                onClick={() => handleDelete(shot.id)}
                className="absolute -top-1.5 -right-1.5 z-10 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                title="Delete screenshot"
              >
                <X className="w-3 h-3" />
              </button>
              {shot.screenshot_analysis && (
                <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-blue-500/60 rounded-b" title="AI analysis available" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload zone (hidden when at limit and uploading) */}
      {!atLimit && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`
            relative border border-dashed rounded-lg p-4 flex flex-col items-center justify-center gap-2
            cursor-pointer transition-colors text-center
            ${dragOver
              ? 'border-blue-500 bg-blue-500/5'
              : 'border-border hover:border-muted-foreground hover:bg-accent/30'
            }
            ${uploading ? 'cursor-not-allowed opacity-70' : ''}
          `}
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
              <p className="text-sm text-muted-foreground">
                Uploading{canAnalyze ? ' & analyzing' : ''}… {uploadProgress}%
              </p>
              <div className="w-full max-w-xs h-1 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {dragOver
                  ? 'Drop to upload'
                  : `Click or drag to add screenshot${remaining > 1 ? 's' : ''}`}
              </p>
              {remaining < limit && (
                <p className="text-xs text-muted-foreground">
                  {remaining} slot{remaining === 1 ? '' : 's'} remaining
                </p>
              )}
              {canAnalyze && (
                <p className="text-xs text-blue-400 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Charts will be analyzed with AI
                </p>
              )}
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            multiple={limit > 1}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) {
                handleUpload(e.target.files);
                e.target.value = '';
              }
            }}
          />
        </div>
      )}

      {/* At-limit indicator */}
      {atLimit && !uploading && (
        <p className="text-xs text-muted-foreground text-center">
          Screenshot limit reached for your plan ({limit}/{limit}).
          {limit < 3 && (
            <> <span className="text-blue-400">Upgrade to Pro</span> for up to 3 per trade.</>
          )}
        </p>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 text-red-400 text-xs bg-red-500/10 rounded p-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto flex-shrink-0">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ScreenshotUpload;
